import {
  ChannelType,
  type ChannelType as TChannelType,
} from 'src/api/generated';
import { motion } from 'framer-motion';
import { PiCaretDownLight, PiCheckLight, PiXLight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Loading from 'src/modules/shared/components/atoms/loading';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Dropdown from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { ChannelIcon } from 'src/modules/shared/components/atoms/icons/channel-icon';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { ComponentSize } from 'src/modules/shared/types/component.type';
import { getContactAddressChannelLabelKey } from 'src/modules/communications/utils/contact-address-channel.util';
import {
  getAddressInputPaddings,
  getAddressInputPattern,
  getAddressInputType,
} from 'src/modules/communications/utils/contact-address-field.util';
import {
  getInlineIconSize,
} from 'src/modules/shared/components/atoms/buttons/button/button.styles';

interface EditableAddressFieldEditorProps {
  allowChannelTypeChange: boolean;
  editedAddress: string;
  editedChannelType: TChannelType;
  loading: boolean;
  onAddressChange: (value: string) => void;
  onCancel: () => void;
  onChannelTypeChange: (value: TChannelType) => void;
  onSave: () => void;
  placeholder: string;
  size: ComponentSize;
}

const getAddressEditorButtonSize = (inputSize: ComponentSize): ComponentSize => {
  switch (inputSize) {
    case '2xs':
      return '2xs';
    case 'xs':
      return '2xs';
    case 'sm':
      return '2xs';
    case 'md':
      return 'xs';
    case 'lg':
      return 'sm';
    default:
      return 'xs';
  }
};

export const EditableAddressFieldEditor = ({
  allowChannelTypeChange,
  editedAddress,
  editedChannelType,
  loading,
  onAddressChange,
  onCancel,
  onChannelTypeChange,
  onSave,
  placeholder,
  size,
}: EditableAddressFieldEditorProps) => {
  const { t } = useI18n();
  const buttonSize = getAddressEditorButtonSize(size);
  const secondaryIconSize = getInlineIconSize(buttonSize);
  const channelTypeOptions: { value: ChannelType; label: string }[] = [
    {
      value: ChannelType.EMAIL,
      label: t(getContactAddressChannelLabelKey(ChannelType.EMAIL)),
    },
    {
      value: ChannelType.PHONE,
      label: t(getContactAddressChannelLabelKey(ChannelType.PHONE)),
    },
  ];
  const {
    leftPadding: textInputLeftPadding,
    rightPadding: textInputRightPadding,
  } = getAddressInputPaddings(size, allowChannelTypeChange);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1 w-full"
    >
      <div className="relative bg-gray-50 rounded-lg w-full">
        {allowChannelTypeChange && (
          <div className="absolute left-1 top-1/2 z-10 -translate-y-1/2">
            <Dropdown
              handler={
                <Button
                  variant="secondary"
                  appearance="ghost"
                  disabled={loading}
                  size={buttonSize}
                  className="flex items-center gap-1.5"
                >
                  <ChannelIcon channelType={editedChannelType} />
                  <PiCaretDownLight className={secondaryIconSize} />
                </Button>
              }
              items={channelTypeOptions.map((option) => ({
                icon: <ChannelIcon channelType={option.value} />,
                content: option.label,
                onClick: () => onChannelTypeChange(option.value),
                disabled: loading,
                className: 'w-4',
              }))}
              size={buttonSize}
              placement="bottom-start"
            />
          </div>
        )}
        <TextInput
          className="w-full !py-0"
          value={editedAddress}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onAddressChange(e.target.value)
          }
          style={{
            paddingLeft:
              textInputLeftPadding == null
                ? undefined
                : `${textInputLeftPadding}rem`,
            paddingRight:
              textInputRightPadding == null
                ? undefined
                : `${textInputRightPadding}rem`,
          }}
          placeholder={placeholder}
          shadow="inset-sm"
          disabled={loading}
          type={getAddressInputType(editedChannelType)}
          pattern={getAddressInputPattern(editedChannelType)}
          size={size}
        />
        <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
          <Button
            variant="neutral"
            appearance="ghost"
            onClick={onCancel}
            disabled={loading}
            isIconOnly
            size={buttonSize}
          >
            <PiXLight className={secondaryIconSize} />
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={loading}
            isIconOnly
            size={buttonSize}
          >
            {loading ? (
              <Loading size="2xs" variant="primary" appearance="outline" />
            ) : (
              <PiCheckLight className={secondaryIconSize} />
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
